import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import ErrorOutlineOutlinedIcon from '@material-ui/icons/ErrorOutlineOutlined';
import { Typography, Grid, Divider } from '@material-ui/core';
import { InfoWidget } from '@ds/react-components';
import { connect } from 'react-redux';
import EditAddress from './EditAddress.js';
import EditEmergencyContact from './EditEmergencyContact.js';
import { actionCreators } from './store.js';

const styles = (theme) => ({
  root: {},
  name: {
    marginBottom: 20,
  },
  data: {
    minHeight: 40,
  },
  dataLeft: {
    '& p': {
      color: theme.palette.common.black,
    },
  },
  dataRight: {
    '& p': {
      color: theme.palette.common.black,
    },
    textAlign: 'right',
  },
  dataType: {
    '& p': {
      color: theme.palette.common.lightText,
    },
  },
  divider: {
    marginTop: 20,
    marginBottom: 20,
  },
  icon: {
    fontSize: 18,
    verticalAlign: 'text-top',
  },
  expandable: {
    cursor: 'pointer',
    '& svg': {
      marginRight: '-5px',
      marginLeft: 5,
      marginTop: 2,
    },
    '& a': {
      textDecoration: 'underline',
    },
  },
  indent: {
    padding: '10px 18px',
    '& a': {
      textDecoration: 'underline',
    },
    backgroundColor: '#f9fcfd',
  },
  copyButton: {
    padding: 0,
    marginLeft: 3,
    '& svg path': {
      fill: theme.palette.common.darkerText,
    },
  },
  tags: {
    display: 'flex',
    '& span': {
      marginRight: 5,
    },
  },
  editLink: {
    color: theme.palette.common.darkerText,
    '& path': {
      fill: theme.palette.common.darkerText,
    },
  },
  editText: {
    fontSize: '12px',
  },
});

const getCountryCode = (value) => {
  if (value) return value.trim() === 'NZ' ? '+64' : '+61';
};

const getPhoneDetails = (value, country) => {
  let countryCodeDetails = '';
  const reg = /^0+/;
  if (country) {
    countryCodeDetails = getCountryCode(country);
  }

  if (value === undefined || value.length < 1) {
    return null;
  }

  //remove 0 from the numbers when they have leading zero
  const removedLeadingZero = value.map((contact) =>
    reg.test(contact.number)
      ? { ...contact, number: contact.number.replace(reg, '') }
      : contact
  );

  let phoneContacts = removedLeadingZero;

  const result = phoneContacts.map((contact) =>
    contact.countryCode &&
    country.id !== null &&
    (contact.countryCode === '+61' || contact.countryCode === '+64')
      ? contact
      : { ...contact, countryCode: countryCodeDetails }
  );

  return result[0];
};

class Address extends React.Component {
  loadAddressRegions = (countryCode) => {
    const { getAddressRegion } = this.props;
    getAddressRegion(countryCode);
  };

  getAddress = (profileDetails) => {
    if (profileDetails.addresses.length === 0) return null;

    return profileDetails.addresses[0];
  };

  isNotAnytimeFit = (profileDetails) => {
    return profileDetails.facilityGroupName !== 'Anytime Fitness';
  };

  render() {
    const { classes, profileDetails } = this.props;

    const contact =
      profileDetails && profileDetails.hasOwnProperty('emergencyContacts')
        ? getPhoneDetails(
            profileDetails.emergencyContacts,
            profileDetails.country
          )
        : null;

    let contactElement;

    if (contact === null) {
      contactElement = (
        <Grid item xs={12} className={classes.dataType}>
          <Typography>None</Typography>
        </Grid>
      );
    } else {
      contactElement = (
        <div>
          <div>
            <Grid container className={classes.data}>
              <Grid item xs={12} className={classes.dataContent}>
                <Typography>
                  {contact.contactName ? contact.contactName : '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} className={classes.dataType}>
                <Typography>Full name</Typography>
              </Grid>
            </Grid>
          </div>
          <Divider className={classes.divider} />
          <div>
            <Grid container className={classes.data}>
              <Grid item xs={12} className={classes.dataContent}>
                <Typography>
                  {contact.number && /^[0-9]+$/.test(contact.number)
                    ? contact.countryCode + ' ' + contact.number
                    : '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} className={classes.dataType}>
                <Typography>Phone</Typography>
              </Grid>
            </Grid>
          </div>
        </div>
      );
    }

    let emergencyTitle = (
      <div>
        Emergency Contact
        {contact !== null &&
          (contact.contactName === '' ||
            contact.number === '' ||
            contact.contactName === null ||
            contact.number === null) && (
            <Tooltip title="Information incomplete" placement="top">
              <ErrorOutlineOutlinedIcon
                fontSize="small"
                style={{
                  color: '#E5AD12',
                  marginLeft: '5px',
                  verticalAlign: 'bottom',
                }}></ErrorOutlineOutlinedIcon>
            </Tooltip>
          )}
      </div>
    );

    let address = profileDetails.hasOwnProperty('addresses')
      ? this.getAddress(profileDetails)
      : null;

    let addressElement;

    if (address === null) {
      addressElement = (
        <Grid item xs={12} className={classes.dataType}>
          <Typography>None</Typography>
        </Grid>
      );
    } else {
      addressElement = (
        <Grid item xs={12} className={classes.dataLeft}>
          <Typography>{address.streetNumber}</Typography>
          {address.street && <Typography>{address.street}</Typography>}
          {address.country === 'New Zealand' && address.suburb && (
            <Typography>{address.suburb}</Typography>
          )}
          {address.country === 'Australia' &&
            (address.suburb ? (
              <Typography>{address.suburb}</Typography>
            ) : (
              <Typography>{address.city}</Typography>
            ))}
          {address.country === 'New Zealand' && (
            <Typography>
              {address.city === 'none' ? '' : address.city} {address.postcode}
            </Typography>
          )}
          {address.country === 'Australia' && (
            <Typography>
              {address.state === 'none' ? '' : address.state} {address.postcode}
            </Typography>
          )}
          <Typography>{address.country}</Typography>
        </Grid>
      );
    }

    return (
      <Grid container direction="column" spacing={3}>
        <Grid item>
          {address !== null && this.isNotAnytimeFit(profileDetails) && (
            <EditAddress
              linkText="Edit"
              currentAddress={address}
              title="Edit this address"
              profileDetails={profileDetails}
              loadRegion={this.loadAddressRegions}
            />
          )}
          <InfoWidget
            title={'Address'}
            isLoading={!profileDetails.hasOwnProperty('addresses')}
            classes={{ root: classes.root }}>
            <Grid container className={classes.data}>
              {addressElement}
            </Grid>
          </InfoWidget>
        </Grid>
        <Grid item>
          {contact !== null && this.isNotAnytimeFit(profileDetails) && (
            <EditEmergencyContact
              linkText="Edit"
              currentContact={contact}
              title="Edit your emergency contact"
              profileDetails={profileDetails}
            />
          )}
          <InfoWidget
            title={emergencyTitle}
            isLoading={!profileDetails.hasOwnProperty('emergencyContacts')}
            classes={{ root: classes.root }}>
            {contactElement}
          </InfoWidget>
        </Grid>
      </Grid>
    );
  }
}

Address.propTypes = {
  classes: PropTypes.object.isRequired,
  profileDetails: PropTypes.object.isRequired,
};

const mapDispatchToProps = {
  getAddressRegion: actionCreators.getAddressRegion,
};

export default connect(null, mapDispatchToProps)(withStyles(styles)(Address));
